import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ruRU } from '@mui/x-date-pickers/locales'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { Controller, useForm } from 'react-hook-form'
import { useActions } from 'store/hooks'
import { MenuItem, SelectDropdown, DatePicker } from 'ui'

import { orderApiSpace } from '../namespaces/orderApiSpace'
import { orderFormSpace } from '../namespaces/orderFormSpace'

import style from './OrderDateInputs.module.scss'

dayjs.extend(isBetween)

type OrderDateInputsProps = {
  item: orderFormSpace.TimeSlot
  data: orderApiSpace.TimeSlotList
}

export function OrderDateInputs({ item, data }: OrderDateInputsProps) {
  const { control } = useForm()
  const { updateTimeSlotOrder } = useActions()

  const timeSlots = [...data.list]

  const isValidDate = (value: Date) => {
    return dayjs(value).isValid()
  }

  const isAvailableDate = (value: Date) => {
    return dayjs(value).isBetween(dayjs(), dayjs().add(2, 'year'), 'day', '[]')
  }

  const today = dayjs()
  const yearLatter = dayjs().add(1, 'year')

  return (
    <>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale='ru'
        localeText={
          ruRU.components.MuiLocalizationProvider.defaultProps.localeText
        }
      >
        <Controller
          name={`date-${item.key}`}
          control={control}
          render={() => (
            <DatePicker
              autoFocus={false}
              className={style.datePicker}
              yearsPerRow={3}
              slotProps={{
                field: { clearable: true },
                actionBar: {
                  actions: ['today', 'clear'],
                },
                textField: {
                  inputProps: {
                    'data-testid': `order-date-picker-${item.key}`,
                  },
                },
              }}
              onAccept={(newDate: Date) => {
                if (!isValidDate(newDate) || !isAvailableDate(newDate)) return

                updateTimeSlotOrder({
                  key: item.key,
                  date: dayjs(newDate).format('YYYY-MM-DD'),
                  isValidDate: true,
                })
              }}
              onChange={newDate => {
                updateTimeSlotOrder({
                  key: item.key,
                  date: isValidDate(newDate)
                    ? dayjs(newDate).format('YYYY-MM-DD')
                    : null,
                  isValidDate:
                    isAvailableDate(newDate) && isValidDate(newDate)
                      ? true
                      : false,
                })
              }}
              views={['year', 'month', 'day']}
              minDate={today}
              maxDate={yearLatter}
              value={item?.date ? dayjs(item.date) : null}
              label='Дата'
            />
          )}
        />
      </LocalizationProvider>
      <Controller
        name={`timePick-${item.key}`}
        control={control}
        render={({ field }) => (
          <SelectDropdown
            selectProps={{
              ...field,
              label: 'Время',
              value: item.value,
              inputProps: { 'data-testid': `order-time-select-${item.key}` },
            }}
            formControlProps={{ sx: { minWidth: '100%' } }}
          >
            {timeSlots
              .sort(
                (a, b) =>
                  Number(a.from.slice(0, 2)) - Number(b.from.slice(0, 2)),
              )
              .map(current => {
                const currentValue = `с ${current.from} до ${current.to}`
                return (
                  <MenuItem
                    key={current.id}
                    value={currentValue}
                    onClick={() => {
                      updateTimeSlotOrder({
                        key: item.key,
                        id: current.id,
                        value: currentValue,
                      })
                    }}
                  >
                    {currentValue}
                  </MenuItem>
                )
              })}
          </SelectDropdown>
        )}
      />
    </>
  )
}
